import {
  AccessNotice,
  SourceAccountBadge,
} from "./payrollProfilePresentation.js";
import { ProfileContractSettings, ProfileIdentityForm } from "./payrollProfileIdentityForm.js";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  CalendarClock,
  Check,
  CircleAlert,
  History,
  Plus,
  RotateCcw,
  Save,
  Trash2,
  Users,
  X,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import { Link, useParams } from "../lib/navigation.js";

import { useAuth } from "../app/auth.js";
import {
  Badge,
  Button,
  Card,
  Input,
  Skeleton,
  Textarea,
} from "../components/ui/index.js";
import { api, type OpenProjectPayrollProfilesResponse } from "../lib/api.js";
import {
  newPeriodAutomaticReason,
  periodAutomaticReason,
  profileAutomaticReason,
  resolveAuditReason,
} from "../lib/payrollProfileChanges.js";
import { queryClient } from "../lib/queryClient.js";
import {
  normalizePayrollProfileRouteId,
  payrollProfileEmployeeMatchesRoute,
} from "./payrollProfileIdentity.js";

import { PeriodFields } from "./payrollProfileForms.js";
import {
  type OperationDiagnostic,
  type PayrollPeriodDraft,
  type PayrollProfileDraft,
  type PayrollProfilePeriod,
  type PayrollUser,
  type ProfileFeedback,
  type VacationOpeningBalance,
  type VacationOpeningBalanceDraft,
  applicableAllowancePeriod,
  auditReasonError,
  auditReasonHint,
  currentMonth,
  defaultPeriodDraft,
  defaultVacationOpeningBalanceDraft,
  diagnosticFromError,
  draftFromPeriod,
  employeeName,
  errorMessage,
  formatChangedAt,
  payrollContextSearch,
  payrollMonthLabel,
  periodPayload,
  profileFromUser,
  validatePeriod,
  validateProfile,
  validateVacationOpeningBalance,
} from "./payrollProfileModel.js";

export const PayrollEmployeeProfilePage = () => {
  const authQuery = useAuth();
  const currentUser = authQuery.data?.user;
  const params = useParams();
  const employeeId = normalizePayrollProfileRouteId(params.employeeId);
  const payrollAccessQuery = useQuery<{ officeAccess: boolean }>({
    queryKey: ["openproject", "payroll-access"],
    queryFn: () => api.getOpenProjectPayrollAccess(),
    enabled: Boolean(currentUser),
    retry: false,
  });
  const hasPayrollAccess = Boolean(payrollAccessQuery.data?.officeAccess);
  const [payrollContext] = useState(() =>
    payrollContextSearch(currentUser?.id),
  );
  const eligibilityMonth = currentMonth();
  const recoveryMonth = payrollContext.month ?? eligibilityMonth;
  const openingBalanceRecovery = window.location.hash === "#vacation-balance";
  useEffect(() => {
    const url = new URL(window.location.href);
    for (const key of ["month", "stage", "payslipStep", "periodThrough"])
      url.searchParams.delete(key);
    window.history.replaceState(
      {
        ...(window.history.state ?? {}),
        payrollProfileRecovery: payrollContext,
      },
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  }, [payrollContext]);
  const resolvingMissingPayslip = payrollContext.resolve === "missing-payslip";

  const profilesQuery = useQuery<
    OpenProjectPayrollProfilesResponse,
    Error,
    { users: PayrollUser[] }
  >({
    queryKey: ["openproject", "payroll-profiles", "directory"],
    queryFn: () => api.getOpenProjectPayrollProfiles(),
    enabled: Boolean(currentUser && hasPayrollAccess),
    refetchOnMount: "always",
  });

  const [profileDrafts, setProfileDrafts] = useState<
    Record<string, PayrollProfileDraft>
  >({});
  const [newPeriodDrafts, setNewPeriodDrafts] = useState<
    Record<string, PayrollPeriodDraft>
  >({});
  const [periodDrafts, setPeriodDrafts] = useState<
    Record<number, PayrollPeriodDraft>
  >({});
  const [vacationOpeningBalanceDrafts, setVacationOpeningBalanceDrafts] =
    useState<Record<string, VacationOpeningBalanceDraft>>({});
  const [addPeriodOpen, setAddPeriodOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PayrollProfilePeriod | null>(
    null,
  );
  const [deleteReason, setDeleteReason] = useState("");
  const [feedback, setFeedback] = useState<ProfileFeedback | null>(null);
  const [diagnostics, setDiagnostics] = useState<OperationDiagnostic[]>([]);

  const reportFailure = (operation: string, error: unknown) => {
    const diagnostic = diagnosticFromError(operation, error);
    setDiagnostics((current) => [diagnostic, ...current].slice(0, 20));
    setFeedback({
      tone: "error",
      message: errorMessage(error),
      diagnostic,
    });
  };

  const allUsers = useMemo(
    () => profilesQuery.data?.users ?? [],
    [profilesQuery.data?.users],
  );
  const selectedUser =
    allUsers.find((user) =>
      employeeId
        ? payrollProfileEmployeeMatchesRoute(user.id, employeeId)
        : false,
    ) ?? null;
  const selectedProfile = selectedUser
    ? (profileDrafts[selectedUser.id] ?? profileFromUser(selectedUser))
    : null;
  const allowanceFromPeriod = selectedUser
    ? applicableAllowancePeriod(selectedUser)
    : undefined;
  useEffect(() => {
    if (!selectedUser || window.location.hash !== "#vacation-balance") return;
    window.requestAnimationFrame(() => {
      const target = document.getElementById("vacation-balance");
      target?.setAttribute("open", "");
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      target?.focus({ preventScroll: true });
    });
  }, [selectedUser]);
  const profileEdited = selectedUser
    ? Boolean(profileDrafts[selectedUser.id])
    : false;
  const selectedPeriods = useMemo(
    () =>
      [...(selectedUser?.profilePeriods ?? [])].sort(
        (left, right) =>
          right.validFromMonth.localeCompare(left.validFromMonth) ||
          right.id - left.id,
      ),
    [selectedUser?.profilePeriods],
  );

  const invalidatePayroll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["openproject", "payroll-profiles"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["openproject", "pohoda-preview"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["openproject", "pohoda-exports"],
      }),
      queryClient.invalidateQueries({ queryKey: ["openproject", "payslips"] }),
      queryClient.invalidateQueries({ queryKey: ["openproject", "balances"] }),
      queryClient.invalidateQueries({
        queryKey: ["openproject", "payroll-workflow"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["openproject", "payroll-audit"],
      }),
    ]);
  };

  const profileMutation = useMutation({
    mutationFn: ({
      userId,
      draft,
    }: {
      userId: string;
      draft: PayrollProfileDraft;
    }) => {
      const originalUser = allUsers.find((user) => user.id === userId);
      if (!originalUser) throw new Error("Payroll employee was not found.");
      const original = profileFromUser(originalUser);
      return api.updateOpenProjectPayrollProfile(userId, {
        expectedRevision: draft.expectedRevision,
        payrollEmployeeCode: draft.payrollEmployeeCode,
        payrollEmail: draft.payrollEmail,
        ...(draft.payrollPdfPassword
          ? { payrollPdfPassword: draft.payrollPdfPassword }
          : {}),
        ...(draft.payrollCalculationEnabled !==
        original.payrollCalculationEnabled
          ? { payrollCalculationEnabled: draft.payrollCalculationEnabled }
          : {}),
        // Employment start/end are derived from the contract-period timeline
        // (D5), not editable in the UI, so they are intentionally NOT sent on
        // submit. The API keeps accepting them for other callers/tests.
        ...(!applicableAllowancePeriod(originalUser) &&
        Number.parseFloat(draft.payrollAnnualHolidayEntitlement) !==
          Number.parseFloat(original.payrollAnnualHolidayEntitlement)
          ? {
              payrollAnnualHolidayEntitlement: Number.parseFloat(
                draft.payrollAnnualHolidayEntitlement,
              ),
            }
          : {}),
        reason: draft.reason,
      });
    },
    onSuccess: async (_data, variables) => {
      setProfileDrafts((drafts) => {
        const next = { ...drafts };
        delete next[variables.userId];
        return next;
      });
      setFeedback({
        tone: "success",
        message: resolvingMissingPayslip
          ? `Work Profile saved for ${payrollMonthLabel(recoveryMonth)}. Return to payroll; if eligibility changed, regenerate the now-stale Pohoda input.`
          : "Current payroll profile saved.",
      });
      await invalidatePayroll();
    },
    onError: (error) => reportFailure("Save current employment", error),
  });

  const createPeriodMutation = useMutation({
    mutationFn: ({
      userId,
      draft,
    }: {
      userId: string;
      draft: PayrollPeriodDraft;
    }) =>
      api.createOpenProjectPayrollProfilePeriod(userId, periodPayload(draft)),
    onSuccess: async (_data, variables) => {
      setNewPeriodDrafts((drafts) => {
        const next = { ...drafts };
        delete next[variables.userId];
        return next;
      });
      setAddPeriodOpen(false);
      setFeedback({
        tone: "success",
        message: "Effective-dated payroll period added.",
      });
      await invalidatePayroll();
    },
    onError: (error) => reportFailure("Add contract period", error),
  });
  const vacationOpeningBalanceMutation = useMutation({
    mutationFn: ({
      userId,
      draft,
      expectedRevision,
    }: {
      userId: string;
      draft: VacationOpeningBalanceDraft;
      expectedRevision: number | null;
    }) =>
      api.saveOpenProjectVacationOpeningBalance(userId, {
        effectiveMonth: draft.effectiveMonth,
        days: Number.parseFloat(draft.days),
        reason: draft.reason.trim(),
        expectedRevision,
      }),
    onSuccess: async (data, variables) => {
      setVacationOpeningBalanceDrafts((drafts) => {
        const next = { ...drafts };
        delete next[variables.userId];
        return next;
      });
      const employee = data["employee"] as
        | {
            payrollVacationOpeningBalances?: VacationOpeningBalance[];
          }
        | undefined;
      const saved = employee?.payrollVacationOpeningBalances?.find(
        (balance) =>
          balance.year === Number(variables.draft.effectiveMonth.slice(0, 4)),
      );
      setFeedback({
        tone: "success",
        message: `Vacation opening balance saved. It applies from ${saved?.appliesFromMonth ?? variables.draft.effectiveMonth}; closed months remain unchanged.`,
      });
      await invalidatePayroll();
    },
    onError: (error) => reportFailure("Save vacation opening balance", error),
  });

  const updatePeriodMutation = useMutation({
    mutationFn: ({
      userId,
      periodId,
      draft,
    }: {
      userId: string;
      periodId: number;
      draft: PayrollPeriodDraft;
    }) =>
      api.updateOpenProjectPayrollProfilePeriod(
        userId,
        periodId,
        periodPayload(draft),
      ),
    onSuccess: async (_data, variables) => {
      setPeriodDrafts((drafts) => {
        const next = { ...drafts };
        delete next[variables.periodId];
        return next;
      });
      setFeedback({
        tone: "success",
        message: "Payroll period updated and recorded in the audit trail.",
      });
      await invalidatePayroll();
    },
    onError: (error) => reportFailure("Update contract period", error),
  });

  const deletePeriodMutation = useMutation({
    mutationFn: async ({
      userId,
      period,
      reason,
    }: {
      userId: string;
      period: PayrollProfilePeriod;
      reason: string;
    }) => {
      await api.updateOpenProjectPayrollProfilePeriod(
        userId,
        period.id,
        periodPayload({
          ...draftFromPeriod(period, selectedUser?.revision),
          ...(selectedUser?.revision === undefined
            ? {}
            : { expectedRevision: selectedUser.revision }),
          reason,
        }),
      );
      return api.deleteOpenProjectPayrollProfilePeriod(userId, period.id, "");
    },
    onSuccess: async () => {
      setDeleteTarget(null);
      setDeleteReason("");
      setFeedback({
        tone: "success",
        message: "Payroll period deleted with its audit reason.",
      });
      await invalidatePayroll();
    },
    onError: (error) => reportFailure("Delete contract period", error),
  });

  const busy =
    profileMutation.isPending ||
    createPeriodMutation.isPending ||
    updatePeriodMutation.isPending ||
    deletePeriodMutation.isPending ||
    vacationOpeningBalanceMutation.isPending;

  if (authQuery.isLoading || (currentUser && payrollAccessQuery.isLoading)) {
    return (
      <Card elevated>
        <div
          className="space-y-3"
          role="status"
          aria-label="Loading employee payroll profile"
        >
          <Skeleton className="h-5 w-56 rounded-lg" />
          <Skeleton className="h-44 w-full rounded-xl" />
          <Skeleton className="h-52 w-full rounded-xl" />
        </div>
      </Card>
    );
  }

  if (!currentUser) {
    return (
      <AccessNotice title="Sign in required">
        Sign in before opening a payroll employee profile.
      </AccessNotice>
    );
  }

  if (payrollAccessQuery.isError || !hasPayrollAccess) {
    return (
      <AccessNotice title="Payroll access required">
        OpenProject payroll office access is required to manage Work profiles.
      </AccessNotice>
    );
  }

  if (profilesQuery.isLoading) {
    return (
      <Card elevated>
        <div
          className="space-y-3"
          role="status"
          aria-label="Loading employee payroll profile"
        >
          <Skeleton className="h-6 w-64 rounded-lg" />
          <Skeleton className="h-44 w-full rounded-xl" />
          <Skeleton className="h-52 w-full rounded-xl" />
        </div>
      </Card>
    );
  }

  if (profilesQuery.isError) {
    return (
      <AccessNotice title="Work profiles unavailable">
        {errorMessage(profilesQuery.error)}
      </AccessNotice>
    );
  }

  if (!employeeId || !selectedUser) {
    return (
      <Card className="mx-auto max-w-2xl" elevated>
        <div className="flex items-start gap-3">
          <span className="rounded-xl bg-warning-subtle p-2 text-warning">
            <CircleAlert aria-hidden="true" size={20} />
          </span>
          <div>
            <h1 className="text-lg font-semibold text-foreground">
              Employee profile not found
            </h1>
            <p className="mt-1 text-sm text-foreground-muted">
              The employee ID is invalid or no longer belongs to an active
              payroll employee.
            </p>
            <Link
              to="/work-profiles"
              className="mt-4 inline-flex min-h-11 items-center gap-2 text-sm font-medium text-accent hover:text-accent-hover"
            >
              <ArrowLeft aria-hidden="true" size={16} /> Back to Work profiles
            </Link>
          </div>
        </div>
      </Card>
    );
  }

  const updateProfileDraft = (patch: Partial<PayrollProfileDraft>) => {
    if (!selectedUser) return;
    setProfileDrafts((drafts) => ({
      ...drafts,
      [selectedUser.id]: {
        ...(drafts[selectedUser.id] ?? profileFromUser(selectedUser)),
        ...patch,
      },
    }));
  };
  const newPeriodDraft = selectedUser
    ? (newPeriodDrafts[selectedUser.id] ?? defaultPeriodDraft(selectedUser))
    : null;
  const vacationOpeningBalanceDraft = selectedUser
    ? (vacationOpeningBalanceDrafts[selectedUser.id] ??
      defaultVacationOpeningBalanceDraft(
        selectedUser,
        openingBalanceRecovery ? recoveryMonth : eligibilityMonth,
      ))
    : null;
  const selectedVacationOpeningBalances = [
    ...(selectedUser?.payrollVacationOpeningBalances ?? []),
  ].sort((left, right) => right.year - left.year || right.id - left.id);
  const updateNewPeriodDraft = (patch: Partial<PayrollPeriodDraft>) => {
    if (!selectedUser) return;
    setNewPeriodDrafts((drafts) => ({
      ...drafts,
      [selectedUser.id]: {
        ...(drafts[selectedUser.id] ?? defaultPeriodDraft(selectedUser)),
        ...patch,
      },
    }));
  };

  const submitProfile = (event: FormEvent) => {
    event.preventDefault();
    if (!selectedUser || !selectedProfile) return;
    const validationError = validateProfile(selectedProfile);
    if (validationError) {
      setFeedback({ tone: "error", message: validationError });
      return;
    }
    if (!profileDrafts[selectedUser.id]) return;
    profileMutation.mutate({
      userId: selectedUser.id,
      draft: {
        ...selectedProfile,
        reason: resolveAuditReason(
          selectedProfile.reason,
          profileAutomaticReason(selectedUser, selectedProfile),
        ),
      },
    });
  };

  const submitNewPeriod = (event: FormEvent) => {
    event.preventDefault();
    if (!selectedUser || !newPeriodDraft) return;
    const validationError = validatePeriod(newPeriodDraft);
    if (validationError) {
      setFeedback({ tone: "error", message: validationError });
      return;
    }
    createPeriodMutation.mutate({
      userId: selectedUser.id,
      draft: {
        ...newPeriodDraft,
        reason: resolveAuditReason(
          newPeriodDraft.reason,
          newPeriodAutomaticReason(newPeriodDraft),
        ),
      },
    });
  };
  const submitVacationOpeningBalance = (event: FormEvent) => {
    event.preventDefault();
    if (!selectedUser || !vacationOpeningBalanceDraft) return;
    const validationError = validateVacationOpeningBalance(
      vacationOpeningBalanceDraft,
    );
    if (validationError) {
      setFeedback({ tone: "error", message: validationError });
      return;
    }
    const existing = selectedUser.payrollVacationOpeningBalances?.find(
      (balance) =>
        balance.effectiveMonth === vacationOpeningBalanceDraft.effectiveMonth,
    );
    vacationOpeningBalanceMutation.mutate({
      userId: selectedUser.id,
      draft: vacationOpeningBalanceDraft,
      expectedRevision: existing?.revision ?? null,
    });
  };

  return (
    <div
      className="payroll-profile-ledger payroll-profile-ledger--detail space-y-3"
      data-page-layout="fiori-floorplan-profile-detail"
    >
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <Link
            to="/work-profiles"
            className="inline-flex min-h-9 items-center gap-2 text-sm font-medium text-foreground-muted transition-colors duration-200 hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:ring-accent focus-visible:ring-offset-2"
          >
            <ArrowLeft aria-hidden="true" size={16} /> Back to Work profiles
          </Link>
          <div className="mt-1 flex items-center gap-2.5">
            <h1 className="text-xl font-semibold tracking-tight text-foreground">
              {selectedUser
                ? `Detail of profile ${employeeName(selectedUser)}`
                : "Employee payroll profile"}
            </h1>
          </div>
        </div>
        {selectedUser && (
          <div className="mt-1 flex items-center gap-2">
            <SourceAccountBadge user={selectedUser} />
            <span className="text-xs text-foreground-muted">
              Import {selectedUser.isActive ? "included" : "excluded"}
            </span>
          </div>
        )}
      </header>

      {resolvingMissingPayslip && selectedUser && (
        <div
          role="status"
          className="rounded-xl border border-warning/40 bg-warning-subtle px-4 py-3"
        >
          <div className="flex items-start gap-3">
            <CircleAlert
              aria-hidden="true"
              size={19}
              className="mt-0.5 shrink-0 text-warning"
            />
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-foreground">
                Resolve the missing {payrollMonthLabel(recoveryMonth)} payslip
              </h2>
              <p className="mt-1 text-sm leading-6 text-foreground-muted">
                The payroll service includes {employeeName(selectedUser)} for
                this month, but no confirmed payslip exists. Upload the PDF, or
                change this Work Profile only when the person should not be in
                payroll. Eligibility changes preserve prior files and require a
                new Pohoda version.
              </p>
              <Link
                to="/"
                search={{
                  month: recoveryMonth,
                  periodThrough: payrollContext.periodThrough ?? recoveryMonth,
                  stage: "payslips",
                  payslipStep: "upload",
                }}
                className="mt-2 inline-flex min-h-9 items-center gap-2 rounded-lg bg-accent px-3 text-sm font-semibold text-accent-fg transition-colors hover:bg-accent-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
              >
                <ArrowLeft aria-hidden="true" size={15} />
                Open Upload payslips
              </Link>
            </div>
          </div>
        </div>
      )}

      {feedback && (
        <div
          role={feedback.tone === "error" ? "alert" : "status"}
          className={`flex items-start justify-between gap-3 rounded-xl border px-4 py-3 text-sm ${feedback.tone === "error" ? "border-danger/40 bg-danger-subtle text-danger" : "border-success/40 bg-success-subtle text-success"}`}
        >
          <div className="flex min-w-0 items-start gap-2">
            {feedback.tone === "error" ? (
              <CircleAlert
                aria-hidden="true"
                className="mt-0.5 shrink-0"
                size={16}
              />
            ) : (
              <Check aria-hidden="true" className="mt-0.5 shrink-0" size={16} />
            )}
            <div className="min-w-0">
              <p>{feedback.message}</p>
              {feedback.diagnostic?.correlationId ? (
                <p className="mt-1 font-mono text-xs">
                  Reference: {feedback.diagnostic.correlationId}
                </p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setFeedback(null)}
            className="-m-1 cursor-pointer rounded-lg p-1 hover:bg-black/10"
            aria-label="Dismiss message"
          >
            <X aria-hidden="true" size={16} />
          </button>
        </div>
      )}

      {diagnostics.length ? (
        <Card elevated padding="sm">
          <details open>
            <summary className="cursor-pointer text-sm font-semibold text-foreground">
              Troubleshooting log ({diagnostics.length})
            </summary>
            <p className="mt-2 text-xs text-foreground-muted">
              Safe request details from this page visit. Use the correlation ID
              to find the matching entry in the payday-api container logs.
            </p>
            <ol className="mt-3 space-y-2">
              {diagnostics.map((diagnostic, index) => (
                <li
                  key={`${diagnostic.occurredAt}-${diagnostic.operation}-${index}`}
                  className="rounded-lg border border-border bg-elevated px-3 py-2 text-xs"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <strong className="text-foreground">
                      {diagnostic.operation}
                    </strong>
                    <time
                      className="tabular-nums text-foreground-muted"
                      dateTime={diagnostic.occurredAt}
                    >
                      {formatChangedAt(diagnostic.occurredAt)}
                    </time>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 font-mono text-foreground-muted">
                    {diagnostic.statusCode ? (
                      <span>HTTP {diagnostic.statusCode}</span>
                    ) : null}
                    {diagnostic.code ? <span>{diagnostic.code}</span> : null}
                    {diagnostic.correlationId ? (
                      <span>{diagnostic.correlationId}</span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-foreground-muted">
                    {diagnostic.retryable
                      ? "Retrying this request is safe."
                      : "Review the details before trying again."}
                  </p>
                </li>
              ))}
            </ol>
            <div className="mt-3 flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setDiagnostics([])}
              >
                Clear log
              </Button>
            </div>
          </details>
        </Card>
      ) : null}

      <div className="min-w-0">
        {selectedUser && selectedProfile ? (
          <section
            className="min-w-0 space-y-3"
            aria-label="Selected employee payroll profile"
          >
            <ProfileIdentityForm
              selectedUser={selectedUser}
              selectedProfile={selectedProfile}
              resolvingMissingPayslip={resolvingMissingPayslip}
              submitProfile={submitProfile}
              updateProfileDraft={updateProfileDraft}
            />

            <section
              className="relative rounded-xl border border-border bg-elevated p-2"
              aria-label="Contract periods"
            >
              <h2 className="flex min-h-11 items-center text-sm font-semibold">
                Contract periods
              </h2>
              <div className="absolute right-2 top-2">
                <Button
                  type="button"
                  size="sm"
                  variant={addPeriodOpen ? "ghost" : "outline"}
                  onClick={() => setAddPeriodOpen((open) => !open)}
                  disabled={busy}
                >
                  {addPeriodOpen ? (
                    <X aria-hidden="true" size={15} />
                  ) : (
                    <Plus aria-hidden="true" size={15} />
                  )}
                  {addPeriodOpen ? "Close" : "Add contract period"}
                </Button>
              </div>

              <ProfileContractSettings
                selectedUser={selectedUser}
                selectedProfile={selectedProfile}
                allowanceFromPeriod={allowanceFromPeriod}
                updateProfileDraft={updateProfileDraft}
                profileEdited={profileEdited}
                busy={busy}
                saving={profileMutation.isPending}
                onReset={() => {
                  setProfileDrafts((drafts) => {
                    const next = { ...drafts };
                    delete next[selectedUser.id];
                    return next;
                  });
                  setFeedback(null);
                }}
              />

              <div>
                {addPeriodOpen && newPeriodDraft && (
                  <form
                    onSubmit={submitNewPeriod}
                    className="mb-3 rounded-xl border border-accent/30 bg-accent-subtle p-3"
                    noValidate
                  >
                    <div className="mb-3 flex items-center gap-2">
                      <CalendarClock
                        aria-hidden="true"
                        className="text-accent"
                        size={18}
                      />
                      <h3 className="text-sm font-semibold text-foreground">
                        New effective period
                      </h3>
                    </div>
                    <PeriodFields
                      idPrefix={`new-period-${selectedUser.id}`}
                      draft={newPeriodDraft}
                      onChange={updateNewPeriodDraft}
                    />
                    <div className="mt-3 flex justify-end">
                      <Button
                        type="submit"
                        size="sm"
                        loading={createPeriodMutation.isPending}
                        disabled={busy}
                      >
                        <Plus aria-hidden="true" size={15} /> Add contract
                        period
                      </Button>
                    </div>
                  </form>
                )}

                <div className="space-y-3">
                  {selectedPeriods.map((period, index) => {
                    const draft =
                      periodDrafts[period.id] ??
                      draftFromPeriod(period, selectedUser?.revision);
                    const changed = Boolean(periodDrafts[period.id]);
                    return (
                      <details
                        open
                        key={period.id}
                        className="rounded-xl border border-border bg-elevated p-2"
                        aria-label={`Contract period from ${period.validFromMonth}`}
                      >
                        <summary className="mb-1 flex min-h-8 cursor-pointer flex-wrap items-center gap-2 text-xs text-foreground-muted">
                          <span className="whitespace-nowrap tabular-nums">
                            {period.validFromMonth} →{" "}
                            {period.validToMonth || "Present"}
                          </span>
                          {index === 0 && (
                            <Badge variant="accent">Latest</Badge>
                          )}
                          {period.boundaryInferred && (
                            <Badge variant="warning">inferred</Badge>
                          )}
                          {!period.calculationEnabled && (
                            <Badge variant="warning">Period excluded</Badge>
                          )}
                        </summary>
                        <div>
                          <PeriodFields
                            idPrefix={`period-${period.id}`}
                            draft={draft}
                            onChange={(patch) =>
                              setPeriodDrafts((drafts) => ({
                                ...drafts,
                                [period.id]: {
                                  ...(drafts[period.id] ??
                                    draftFromPeriod(
                                      period,
                                      selectedUser?.revision,
                                    )),
                                  ...patch,
                                },
                              }))
                            }
                          />
                          <div className="mt-2 flex flex-wrap justify-between gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="danger"
                              disabled={busy || selectedPeriods.length <= 1}
                              title={
                                selectedPeriods.length <= 1
                                  ? "At least one payroll period must remain"
                                  : "Delete this effective period"
                              }
                              onClick={() => {
                                setDeleteTarget(period);
                                setDeleteReason("");
                              }}
                            >
                              <Trash2 aria-hidden="true" size={15} /> Delete
                              period
                            </Button>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                disabled={!periodDrafts[period.id] || busy}
                                onClick={() =>
                                  setPeriodDrafts((drafts) => {
                                    const next = { ...drafts };
                                    delete next[period.id];
                                    return next;
                                  })
                                }
                              >
                                <RotateCcw aria-hidden="true" size={15} /> Reset
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                loading={
                                  updatePeriodMutation.isPending &&
                                  updatePeriodMutation.variables?.periodId ===
                                    period.id
                                }
                                disabled={!changed || busy}
                                onClick={() => {
                                  const validationError = validatePeriod(
                                    draft,
                                    true,
                                    changed,
                                  );
                                  if (validationError) {
                                    setFeedback({
                                      tone: "error",
                                      message: validationError,
                                    });
                                    return;
                                  }
                                  updatePeriodMutation.mutate({
                                    userId: selectedUser.id,
                                    periodId: period.id,
                                    draft: {
                                      ...draft,
                                      reason: resolveAuditReason(
                                        draft.reason,
                                        periodAutomaticReason(period, draft),
                                      ),
                                    },
                                  });
                                }}
                              >
                                <Save aria-hidden="true" size={15} /> Correct
                                this period
                              </Button>
                            </div>
                          </div>
                        </div>
                      </details>
                    );
                  })}
                  {selectedPeriods.length === 0 && (
                    <div className="rounded-xl border border-dashed border-border-strong px-4 py-10 text-center">
                      <History
                        aria-hidden="true"
                        className="mx-auto text-foreground-subtle"
                        size={24}
                      />
                      <p className="mt-2 text-sm font-medium text-foreground">
                        No effective periods yet
                      </p>
                      <p className="mt-1 text-xs text-foreground-muted">
                        Add the employee’s first period to establish calculation
                        settings by month.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {openingBalanceRecovery && (
              <details
                open
                id="vacation-balance"
                tabIndex={-1}
                className="payroll-profile-detail__secondary scroll-mt-4 rounded-xl border border-border bg-elevated focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
              >
                <summary className="flex min-h-12 cursor-pointer list-none flex-wrap items-center justify-between gap-2 px-4 py-3">
                  <span className="text-sm font-semibold text-foreground">
                    Vacation balance
                  </span>
                  <span className="text-xs text-foreground-muted">
                    January opening balance and corrections
                  </span>
                </summary>
                <div className="border-t border-border p-3">
                  <Card
                    elevated
                    padding="sm"
                    className="border-0 bg-transparent p-0"
                  >
                    <form onSubmit={submitVacationOpeningBalance} noValidate>
                      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        <Input
                          id={`profile-${selectedUser.id}-vacation-opening-month`}
                          label="Opening balance effective month"
                          type="month"
                          value={
                            vacationOpeningBalanceDraft?.effectiveMonth ?? ""
                          }
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            setVacationOpeningBalanceDrafts((drafts) => ({
                              ...drafts,
                              [selectedUser.id]: {
                                ...(drafts[selectedUser.id] ??
                                  defaultVacationOpeningBalanceDraft(
                                    selectedUser,
                                    openingBalanceRecovery
                                      ? recoveryMonth
                                      : eligibilityMonth,
                                  )),
                                effectiveMonth: event.target.value,
                              },
                            }))
                          }
                          hint="January only"
                          className="tabular-nums"
                        />
                        <Input
                          id={`profile-${selectedUser.id}-vacation-opening-days`}
                          label="Opening vacation balance (days)"
                          type="number"
                          min="0"
                          step="0.01"
                          value={vacationOpeningBalanceDraft?.days ?? ""}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            setVacationOpeningBalanceDrafts((drafts) => ({
                              ...drafts,
                              [selectedUser.id]: {
                                ...(drafts[selectedUser.id] ??
                                  defaultVacationOpeningBalanceDraft(
                                    selectedUser,
                                    openingBalanceRecovery
                                      ? recoveryMonth
                                      : eligibilityMonth,
                                  )),
                                days: event.target.value,
                              },
                            }))
                          }
                          className="tabular-nums"
                        />
                        <div className="flex flex-col gap-1.5">
                          <span className="text-sm font-medium text-foreground">
                            Source
                          </span>
                          <div className="flex min-h-[38px] items-center rounded-xl border border-border bg-elevated px-3 text-sm font-semibold text-foreground">
                            Manual PayDay entry
                          </div>
                        </div>
                        <Textarea
                          id={`profile-${selectedUser.id}-vacation-opening-reason`}
                          label="Audit reason"
                          rows={2}
                          maxLength={500}
                          value={vacationOpeningBalanceDraft?.reason ?? ""}
                          onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                            setVacationOpeningBalanceDrafts((drafts) => ({
                              ...drafts,
                              [selectedUser.id]: {
                                ...(drafts[selectedUser.id] ??
                                  defaultVacationOpeningBalanceDraft(
                                    selectedUser,
                                    openingBalanceRecovery
                                      ? recoveryMonth
                                      : eligibilityMonth,
                                  )),
                                reason: event.target.value,
                              },
                            }))
                          }
                          hint="15–500 characters; the change is recorded in the payroll audit."
                        />
                      </div>
                      <div className="mt-3 flex flex-wrap justify-end gap-2 border-t border-border pt-3">
                        <Button
                          type="submit"
                          size="sm"
                          loading={vacationOpeningBalanceMutation.isPending}
                          disabled={busy}
                        >
                          <Save aria-hidden="true" size={15} /> Save opening
                          balance
                        </Button>
                      </div>
                    </form>
                    <div className="mt-4 border-t border-border pt-3">
                      <h3 className="text-sm font-semibold text-foreground">
                        Recorded opening balances
                      </h3>
                      {selectedVacationOpeningBalances.length ? (
                        <ul className="mt-2 grid gap-2 sm:grid-cols-2">
                          {selectedVacationOpeningBalances.map((balance) => (
                            <li
                              key={balance.id}
                              className="rounded-lg border border-border bg-elevated px-3 py-2 text-sm"
                            >
                              <strong className="tabular-nums text-foreground">
                                {balance.year}: {balance.days.toFixed(2)} days
                              </strong>
                              <span className="mt-1 block text-xs text-foreground-muted">
                                Applies from {balance.appliesFromMonth} ·{" "}
                                {balance.source === "manual"
                                  ? "Manual PayDay entry"
                                  : balance.source}{" "}
                                · recorded by{" "}
                                {balance.createdByName || "Payroll Office"}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="mt-2 rounded-lg border border-warning/35 bg-warning-subtle px-3 py-2 text-sm text-warning">
                          No opening balance is recorded. Enter the January
                          remaining vacation value above before approving a
                          later month.
                        </p>
                      )}
                    </div>
                  </Card>
                </div>
              </details>
            )}
          </section>
        ) : (
          <Card elevated className="min-h-56">
            <div className="flex min-h-44 flex-col items-center justify-center text-center">
              <Users
                aria-hidden="true"
                className="text-foreground-subtle"
                size={26}
              />
              <p className="mt-3 text-sm font-medium text-foreground">
                Select an employee
              </p>
              <p className="mt-1 text-xs text-foreground-muted">
                Their current profile and effective history will appear here.
              </p>
            </div>
          </Card>
        )}
      </div>

      {deleteTarget && selectedUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget &&
              !deletePeriodMutation.isPending
            )
              setDeleteTarget(null);
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-period-title"
            className="w-full max-w-lg rounded-2xl border border-border bg-elevated p-5 shadow-2xl"
          >
            <div className="flex items-start gap-3">
              <span className="rounded-xl bg-danger-subtle p-2 text-danger">
                <Trash2 aria-hidden="true" size={20} />
              </span>
              <div className="min-w-0 flex-1">
                <h2
                  id="delete-period-title"
                  className="text-lg font-semibold text-foreground"
                >
                  Delete payroll period?
                </h2>
                <p className="mt-1 text-sm text-foreground-muted">
                  This removes {deleteTarget.validFromMonth} →{" "}
                  {deleteTarget.validToMonth || "Present"} for{" "}
                  {employeeName(selectedUser)}. Existing audit records remain
                  available.
                </p>
              </div>
              <button
                type="button"
                className="cursor-pointer rounded-lg p-1 text-foreground-muted hover:bg-border hover:text-foreground"
                onClick={() => setDeleteTarget(null)}
                disabled={deletePeriodMutation.isPending}
                aria-label="Close delete confirmation"
              >
                <X aria-hidden="true" size={18} />
              </button>
            </div>
            <div className="mt-4">
              <Textarea
                id="delete-period-reason"
                label="Reason for deleting this period"
                required
                autoFocus
                minLength={15}
                rows={3}
                maxLength={500}
                value={deleteReason}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                  setDeleteReason(event.target.value)
                }
                hint={auditReasonHint(deleteReason)}
              />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setDeleteTarget(null)}
                disabled={deletePeriodMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                size="sm"
                loading={deletePeriodMutation.isPending}
                disabled={Boolean(auditReasonError(deleteReason))}
                onClick={() =>
                  deletePeriodMutation.mutate({
                    userId: selectedUser.id,
                    period: deleteTarget,
                    reason: deleteReason.trim(),
                  })
                }
              >
                <Trash2 aria-hidden="true" size={15} /> Delete period
              </Button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};
